import * as Ably from 'ably';
import * as Sentry from '@sentry/react';
import { isAxiosError } from 'axios';
import { axiosChatInstance } from '../api/api.base';

// Two attempts (plus the pause between them) fit inside the callback deadline.
export const ABLY_AUTH_REQUEST_TIMEOUT_MS = 8000;
export const ABLY_AUTH_REQUEST_RETRIES = 1;
export const ABLY_AUTH_RETRY_DELAY_MS = 1000;
// Cover the chat requests, shared OIDC renewal, and one auth retry. If it
// still fails, Ably moves to `disconnected` and calls authCallback again.
export const ABLY_AUTH_CALLBACK_DEADLINE_MS = 18000;
// Must exceed the callback deadline, but Ably also uses it for attach,
// connect and heartbeat timeouts, so keep it close to the 10 s default.
export const ABLY_REALTIME_REQUEST_TIMEOUT_MS = 20000;
const ABLY_AUTH_SLOW_MS = 5000;
const ABLY_AUTH_SLOW_SAMPLE_RATE = 0.2;

type ChatTokenDetails = Omit<Ably.TokenDetails, 'issued' | 'expires'> & {
	issued: number | string;
	expires: number | string;
};

const isRetryableTokenRequestError = (err: unknown) => {
	if (!isAxiosError(err) || err.code === 'ERR_CANCELED') {
		return false;
	}
	const status = err.response?.status;
	return !status || status === 502 || status === 503 || status === 504;
};

const wait = (ms: number, signal: AbortSignal) =>
	new Promise<void>((resolve, reject) => {
		const timer = setTimeout(resolve, ms);
		signal.addEventListener(
			'abort',
			() => {
				clearTimeout(timer);
				reject(new Error('canceled'));
			},
			{ once: true },
		);
	});

const requestChatToken = async (signal: AbortSignal) => {
	for (let attempt = 0; ; attempt++) {
		try {
			const { data } = await axiosChatInstance.post<ChatTokenDetails>(
				'/api/app/auth/ably/user-channels',
				{},
				{
					timeout: ABLY_AUTH_REQUEST_TIMEOUT_MS,
					signal,
					'axios-retry': { retries: 0 },
				},
			);
			return data;
		} catch (err) {
			if (
				attempt >= ABLY_AUTH_REQUEST_RETRIES ||
				signal.aborted ||
				!isRetryableTokenRequestError(err)
			) {
				throw err;
			}
			await wait(ABLY_AUTH_RETRY_DELAY_MS, signal);
		}
	}
};

// Telemetry only: runs after Ably has its token and must never throw.
const recordTokenRequestDuration = (durationMs: number, ok: boolean) => {
	try {
		Sentry.addBreadcrumb({
			category: 'ably.auth',
			level: ok ? 'info' : 'warning',
			message: `Ably token request ${ok ? 'succeeded' : 'failed'}`,
			data: { durationMs },
		});
		if (
			durationMs > ABLY_AUTH_SLOW_MS &&
			Math.random() < ABLY_AUTH_SLOW_SAMPLE_RATE
		) {
			Sentry.captureMessage('Slow Ably token request', {
				level: 'warning',
				tags: { 'ably.auth.ok': String(ok) },
				extra: { durationMs },
			});
		}
	} catch {
		// Ignore telemetry failures.
	}
};

export const getAblyClient = (userId: number) => {
	return new Ably.Realtime({
		logLevel: 2,
		clientId: userId.toString(),
		authCallback: (_data, callback) => {
			const controller = new AbortController();
			const startedAt = Date.now();
			let settled = false;
			const deadline = setTimeout(() => {
				if (settled) {
					return;
				}
				settled = true;
				controller.abort();
				callback(
					new Ably.ErrorInfo(
						`Ably token request timed out after ${ABLY_AUTH_CALLBACK_DEADLINE_MS}ms`,
						40170,
						401,
					),
					null,
				);
				recordTokenRequestDuration(Date.now() - startedAt, false);
			}, ABLY_AUTH_CALLBACK_DEADLINE_MS);

			const settle = (
				error: Ably.ErrorInfo | null,
				tokenDetails: Ably.TokenDetails | null,
			) => {
				if (settled) {
					return;
				}
				settled = true;
				clearTimeout(deadline);
				callback(error, tokenDetails);
				recordTokenRequestDuration(Date.now() - startedAt, !error);
			};

			requestChatToken(controller.signal)
				.then(data => {
					if (
						!data ||
						typeof data.token !== 'string' ||
						!data.token
					) {
						throw new Error(
							'Ably token endpoint returned invalid token details',
						);
					}
					// The .NET endpoint returns ISO dates; Ably expects milliseconds.
					const issued =
						typeof data.issued === 'string'
							? Date.parse(data.issued)
							: data.issued;
					const expires =
						typeof data.expires === 'string'
							? Date.parse(data.expires)
							: data.expires;
					if (!Number.isFinite(issued) || !Number.isFinite(expires)) {
						throw new Error(
							'Ably token endpoint returned invalid timestamps',
						);
					}
					settle(null, { ...data, issued, expires });
				})
				.catch(err => {
					if (settled) {
						return;
					}
					const error =
						err instanceof Error ? err : new Error(String(err));
					const statusCode = isAxiosError(err)
						? err.response?.status || 401
						: 401;
					settle(
						// ably 2.29 types `cause` as ErrorInfo only, so the axios
						// error text travels in the message instead.
						new Ably.ErrorInfo(
							error.message,
							statusCode === 403 ? 40300 : 40170,
							statusCode,
						),
						null,
					);
				});
		},
		disconnectedRetryTimeout: 5000,
		suspendedRetryTimeout: 10000,
		realtimeRequestTimeout: ABLY_REALTIME_REQUEST_TIMEOUT_MS,
	});
};
